/*
  Predictive search (Shopify Section Rendering API).

  The class lives inside a registration guard so re-executing this script
  (e.g. a theme editor section reload) cannot throw a duplicate-declaration
  SyntaxError or a customElements.define NotSupportedError.
*/
if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends SearchForm {
      // Curated Icebox terms used to supplement Shopify query suggestions.
      static curatedSuggestions = [
        'Diamond Chains',
        'Diamond Pendants',
        'Diamond Necklaces',
        'Diamond Rings',
        'Diamond Earrings',
        'Diamond Bracelets',
        'Tennis Chains',
        'Tennis Bracelets',
        'Cuban Link Chains',
        'Gold Chains',
        'White Gold Chains',
        'Yellow Gold Chains',
        'Rose Gold Jewelry',
        'Engagement Rings',
        'Wedding Bands',
        'Custom Pendants',
        'Letter Pendants',
        'Cross Pendants',
        'Watches',
        'Rolex Watches',
        'AP Watches',
        'Patek Watches',
      ];

      // Session cache + instance counter shared across all instances.
      static sharedCache = {};
      static instanceCount = 0;

      /*
        Per-term quoting (SRCH relevance fix). Shopify's unquoted storefront
        search fuzzy-matches so broadly that gibberish returns hundreds of
        products; quoting each token turns every term into a required exact
        match and removes the noise tail without changing the real top hits.

        - Strip any quotes the user typed so we never emit nested quotes.
        - Split on whitespace, wrap each token in double quotes.
        - prefixLast=true leaves the final token bare so the as-you-type
          dropdown keeps prefix matching (e.g. "cub" still suggests "cuban");
          the submitted results page quotes every token.
      */
      static quoteTerms(raw, prefixLast) {
        var tokens = String(raw == null ? '' : raw)
          .replace(/"/g, ' ')
          .split(/\s+/)
          .filter(Boolean);
        if (!tokens.length) return '';
        return tokens
          .map(function (token, i) {
            if (prefixLast && i === tokens.length - 1) return token;
            return '"' + token + '"';
          })
          .join(' ');
      }

      // Loose mode: the results page's "show loose matches" escape hatch adds
      // ?raw=1, and re-running that query must stay unquoted.
      static isRawMode() {
        try {
          return new URLSearchParams(window.location.search).get('raw') === '1';
        } catch (error) {
          return false;
        }
      }

      constructor() {
        super();
        this.cachedResults = PredictiveSearch.sharedCache;
        this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
        this.isOpen = false;
        this.abortController = null;
        this.searchTerm = '';
        this.instanceId = (PredictiveSearch.instanceCount += 1);

        this.setupEventListeners();
      }

      setupEventListeners() {
        this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
        this.addEventListener('keyup', this.onKeyup.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
      }

      getQuery() {
        return this.input.value.trim();
      }

      getMinQueryLength() {
        return 2;
      }

      onChange() {
        super.onChange();
        const newSearchTerm = this.getQuery();
        if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
          // Remove the results when they are no longer relevant for the new search term
          // so they don't show up when the dropdown opens again
          this.querySelector('[data-predictive-search-results-wrapper]')?.remove();
          this.querySelector('[data-predictive-search-no-results]')?.remove();
          this.removeAttribute('no-results');
        }

        // Update the term asap, don't wait for the predictive search query to finish loading
        this.updateSearchForTerm(this.searchTerm, newSearchTerm);

        this.searchTerm = newSearchTerm;

        if (this.searchTerm.length < this.getMinQueryLength()) {
          // Too short to search: cancel in-flight requests and fall back to
          // the default (trending / popular) content without clearing input.
          this.abortPendingRequest();
          this.close(!this.searchTerm.length);
          return;
        }

        this.getSearchResults(this.searchTerm);
      }

      onFormSubmit(event) {
        if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
          event.preventDefault();
          return;
        }
        // Stage A: submit the query per-term quoted so Shopify stops
        // fuzzy-matching the whole catalog. The visible input is only rewritten
        // for the instant before navigation; the results page re-displays the
        // user's raw text. Loose mode (?raw=1) is left unquoted on purpose.
        if (!PredictiveSearch.isRawMode()) {
          var quoted = PredictiveSearch.quoteTerms(this.getQuery(), false);
          if (quoted) this.input.value = quoted;
        }
      }

      onFormReset(event) {
        super.onFormReset(event);
        if (super.shouldResetForm()) {
          this.searchTerm = '';
          this.abortPendingRequest();
          this.closeResults(true);
        }
      }

      onFocus() {
        const currentSearchTerm = this.getQuery();

        if (currentSearchTerm.length < this.getMinQueryLength()) return;

        if (this.searchTerm !== currentSearchTerm) {
          // Search term was changed from other search input, treat it as a user change
          this.onChange();
        } else if (this.getAttribute('results') === 'true') {
          this.open();
        } else {
          this.getSearchResults(this.searchTerm);
        }
      }

      onFocusOut() {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      }

      onKeyup(event) {
        if (!this.getQuery().length) this.close(true);

        switch (event.code) {
          case 'ArrowUp':
            event.preventDefault();
            this.switchOption('up');
            break;
          case 'ArrowDown':
            event.preventDefault();
            this.switchOption('down');
            break;
          case 'Enter':
            this.selectOption();
            break;
          case 'Escape':
            this.close();
            break;
        }
      }

      onKeydown(event) {
        // Prevent the cursor from moving in the input when using the up and down arrow keys
        if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
          event.preventDefault();
        }
      }

      updateSearchForTerm(previousTerm, newTerm) {
        const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
        if (!searchForTextElement) return;
        const currentButtonText = searchForTextElement.innerText;
        if (!currentButtonText || !previousTerm) return;

        // Escape regex special characters so terms like "14k (gold)" can't throw.
        const escapedTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const occurrences = currentButtonText.match(new RegExp(escapedTerm, 'g'));
        if (!occurrences || occurrences.length > 1) return;

        searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
      }

      switchOption(direction) {
        if (!this.getAttribute('open')) return;

        const moveUp = direction === 'up';
        const selectedElement = this.querySelector('[aria-selected="true"]');

        // Filter out hidden elements thanks to
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
        const allVisibleElements = Array.from(
          this.querySelectorAll('li[role="option"], button.predictive-search__item')
        ).filter((element) => element.offsetParent !== null);
        let activeElementIndex = 0;

        if (moveUp && !selectedElement) return;

        let selectedElementIndex = -1;
        let i = 0;

        while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
          if (allVisibleElements[i] === selectedElement) {
            selectedElementIndex = i;
          }
          i++;
        }

        this.statusElement.textContent = '';

        if (!moveUp && selectedElement) {
          activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
        } else if (moveUp) {
          activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
        }

        if (activeElementIndex === selectedElementIndex) return;

        const activeElement = allVisibleElements[activeElementIndex];

        activeElement.setAttribute('aria-selected', true);
        if (selectedElement) selectedElement.setAttribute('aria-selected', false);

        this.input.setAttribute('aria-activedescendant', activeElement.id);
      }

      selectOption() {
        const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

        if (selectedOption) selectedOption.click();
      }

      abortPendingRequest() {
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
      }

      getSearchResults(searchTerm) {
        // Lowercase the raw term as the cache key. Do not collapse spaces to
        // hyphens: that made "tennis chain" and "tennis-chain" collide and
        // serve each other's cached markup (SRCH-2).
        const queryKey = searchTerm.toLowerCase();
        this.setLiveRegionLoadingState();

        // Abort any in-flight request so a stale response can't overwrite
        // results for a newer query.
        this.abortPendingRequest();

        if (this.cachedResults[queryKey]) {
          this.renderSearchResults(this.cachedResults[queryKey]);
          return;
        }

        this.abortController = new AbortController();

        // Quote every token so the dropdown matches the results page exactly.
        // Leaving a token bare makes the predictive endpoint fuzzy-match
        // (e.g. "rolex" -> "watch roll"/"rolo", "cuban chian" -> cuban chains),
        // which disagrees with the quoted results page. Exact matching keeps the
        // dropdown and the results page consistent; curated suggestions still
        // surface while a word is being typed.
        fetch(
          `${routes.predictive_search_url}?q=${encodeURIComponent(
            PredictiveSearch.quoteTerms(searchTerm, false)
          )}&resources[type]=product,query,collection&resources[limit]=12&resources[limit_scope]=each&section_id=predictive-search`,
          { signal: this.abortController.signal }
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Predictive search request failed (${response.status})`);
            }

            return response.text();
          })
          .then((text) => {
            const resultsMarkup = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('#shopify-section-predictive-search').innerHTML;
            PredictiveSearch.sharedCache[queryKey] = resultsMarkup;
            // Only render if the response still matches what's typed.
            if (searchTerm === this.searchTerm) {
              this.renderSearchResults(resultsMarkup);
            }
          })
          .catch((error) => {
            if (error?.name === 'AbortError' || error?.code === 20) {
              // Request was superseded by a newer query; nothing to do.
              return;
            }
            this.close();
            console.error('[predictive-search]', error);
          });
      }

      setLiveRegionLoadingState() {
        this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
        this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

        this.setLiveRegionText(this.loadingText);
        this.setAttribute('loading', true);
      }

      setLiveRegionText(statusText) {
        this.statusElement.setAttribute('aria-hidden', 'false');
        this.statusElement.textContent = statusText;

        setTimeout(() => {
          this.statusElement.setAttribute('aria-hidden', 'true');
        }, 1000);
      }

      renderSearchResults(resultsMarkup) {
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.scopeResultIds();
        this.associateResultsWithForm();
        this.supplementSuggestions();
        this.toggleAttribute('no-results', !!this.predictiveSearchResults.querySelector('[data-predictive-search-no-results]'));
        this.setAttribute('results', true);

        this.setLiveRegionResults();
        this.open();
      }

      // Multiple predictive-search instances exist (mobile drawer + desktop
      // panel). Suffix injected ids per instance so the document never
      // contains duplicate ids, then point the input's aria attributes at
      // the scoped listbox.
      scopeResultIds() {
        const suffix = `-ps${this.instanceId}`;
        this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
          if (!element.id.endsWith(suffix)) element.id += suffix;
        });
        this.predictiveSearchResults.querySelectorAll('[aria-labelledby]').forEach((element) => {
          const value = element.getAttribute('aria-labelledby');
          if (!value.endsWith(suffix)) element.setAttribute('aria-labelledby', value + suffix);
        });
        const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
        if (listbox) {
          this.input.setAttribute('aria-owns', listbox.id);
          this.input.setAttribute('aria-controls', listbox.id);
        }
      }

      // The results list (including the "Search for '<term>'" submit button)
      // is rendered outside the <form> in the header pill/dropdown/drawer, so
      // the injected type="submit" button has no implicit form owner and does
      // nothing when clicked. Re-associate it via the `form` attribute (SRCH-1).
      associateResultsWithForm() {
        const form = this.input?.form;
        if (!form) return;

        if (!form.id) {
          form.id = `PredictiveSearchForm-${this.instanceId}`;
        }

        this.predictiveSearchResults
          .querySelectorAll('button[type="submit"], #predictive-search-option-search-keywords')
          .forEach((button) => {
            if (button.tagName === 'BUTTON') button.setAttribute('form', form.id);
          });
      }

      // Fill the "Recommended searches" list up to 6 entries with curated
      // Icebox terms that match the current query.
      supplementSuggestions() {
        const list = this.predictiveSearchResults.querySelector('[data-predictive-search-suggestions]');
        if (!list) return;

        const existingTexts = Array.from(list.querySelectorAll('[data-suggestion-text]')).map((item) =>
          (item.dataset.suggestionText || '').trim().toLowerCase()
        );
        const room = 6 - existingTexts.length;

        if (room > 0) {
          let matches = this.getCuratedMatches(this.searchTerm, existingTexts).slice(0, room);

          // Never leave the "Recommended searches" column empty while results
          // are showing: without it the two-column layout collapses and the
          // product cards get squeezed into the narrow suggestions column.
          // Fall back to the top curated terms as generic popular searches.
          if (existingTexts.length === 0 && matches.length === 0) {
            matches = this.constructor.curatedSuggestions.slice(0, room);
          }

          const searchUrl =
            this.input.form?.getAttribute('action') ||
            (typeof routes !== 'undefined' && routes.search_url) ||
            '/search';

          matches.forEach((term, index) => {
            const item = document.createElement('li');
            item.id = `predictive-search-option-curated-${index + 1}-ps${this.instanceId}`;
            item.className = 'predictive-search__list-item';
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', 'false');
            item.dataset.suggestionText = term;

            const link = document.createElement('a');
            link.className = 'predictive-search__item link link--text';
            link.href = `${searchUrl}?q=${encodeURIComponent(term)}`;
            link.tabIndex = -1;

            const text = document.createElement('span');
            text.className = 'predictive-search__item-query-result';
            text.textContent = term;

            link.appendChild(text);
            item.appendChild(link);
            list.appendChild(item);
          });
        }

        const group = list.closest('.predictive-search__result-group--suggestions');
        if (group) group.hidden = list.children.length === 0;
      }

      getCuratedMatches(query, excludeLowercased) {
        const normalized = query.trim().toLowerCase();
        if (!normalized) return [];

        return this.constructor.curatedSuggestions
          .map((term) => {
            const termLower = term.toLowerCase();
            if (excludeLowercased.includes(termLower)) return null;

            let score = null;
            if (termLower.startsWith(normalized)) {
              score = 0;
            } else if (termLower.split(/\s+/).some((word) => word.startsWith(normalized))) {
              score = 1;
            } else if (termLower.includes(normalized)) {
              score = 2;
            }

            return score === null ? null : { term, score };
          })
          .filter(Boolean)
          .sort((a, b) => a.score - b.score)
          .map((entry) => entry.term);
      }

      setLiveRegionResults() {
        this.removeAttribute('loading');
        const countValue = this.querySelector('[data-predictive-search-live-region-count-value]');
        if (countValue) this.setLiveRegionText(countValue.textContent);
      }

      getResultsMaxHeight() {
        const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
        this.resultsMaxHeight = window.innerHeight - headerBottom;
        return this.resultsMaxHeight;
      }

      open() {
        this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
        this.setAttribute('open', true);
        this.input.setAttribute('aria-expanded', true);
        this.isOpen = true;
      }

      close(clearSearchTerm = false) {
        this.closeResults(clearSearchTerm);
        this.isOpen = false;
      }

      closeResults(clearSearchTerm = false) {
        if (clearSearchTerm) {
          this.input.value = '';
          this.removeAttribute('results');
          this.removeAttribute('no-results');
        }
        const selected = this.querySelector('[aria-selected="true"]');

        if (selected) selected.setAttribute('aria-selected', false);

        this.input.setAttribute('aria-activedescendant', '');
        this.removeAttribute('loading');
        this.removeAttribute('open');
        this.input.setAttribute('aria-expanded', false);
        this.resultsMaxHeight = false;
        this.predictiveSearchResults.removeAttribute('style');
      }
    }
  );
}
